import {
    redirectToCommericalTrial,
    redirectToContractSales,
    redirectToPlan,
    redirectToPurchasePayment,
    redirectToPurchasePeriodPaymentPayment,
} from 'businessCommon/src/services';
import { _lang, lang, commonLang, getCurrentLang, getCurrentSystem } from '@umu/ukit-lang';
import './editionItem.scss';
import React, { useCallback, Fragment } from 'react';
import { If, Button } from 'common/src/components';
import { PLAN_TYPE, VERSION_TYPE, IPlanInfo } from '@umu/umu-types';
import { trackEvent } from '../../../utils/util';
import { ACTION_TYPE, DEVICE_FROM_MAP } from '@umu/umu-api/src/services/payment/paymentEvent';

import { setStatLogDialogs } from '@umu/umu-api';
import { useSurfacePricing } from '../../../hooks/useSurfacePricing';
import { applyAppPurchase } from 'plan/@planPricingCommon/utils/util';

const system = getCurrentSystem();
const localeLang = getCurrentLang();

interface IEditionItemProps {
    isRegisterUser: boolean;
    planInfo: IPlanInfo;
    siteHost: string;
    platform: 'twww' | 'mobile';
}
export const EditionItem = (props: IEditionItemProps) => {
    const { isRegisterUser, planInfo, siteHost, platform } = props;

    const surfacePricing = useSurfacePricing();
    const professionalPricing = surfacePricing ? surfacePricing[2] : undefined;
    const teamPricing = surfacePricing ? surfacePricing[3] : undefined;

    const getPremiumPrice = () => {
        if (!professionalPricing) {
            return '';
        }
        return professionalPricing.amount_settle || professionalPricing.amount;
    };
    const getBusinessPrice = () => {
        if (!teamPricing) {
            return '';
        }
        return teamPricing.amount_settle || teamPricing.amount;
    };

    const onPlan = useCallback(
        (planId: number, isBuyNow?: boolean) => {
            if (planId === 2 && applyAppPurchase(planId)) {
                return;
            }
            redirectToPlan(platform, isRegisterUser, planId, isBuyNow);
        },
        [siteHost, isRegisterUser, platform],
    );

    const redirectInCommerical = () => {
        // 团队版试用
        if (Number(planInfo.teacher_status) === VERSION_TYPE.COMMERICAL_TRIAL) {
            redirectToPurchasePayment(platform, PLAN_TYPE.COMMERICAL);
            return;
        }
        redirectToPurchasePeriodPaymentPayment(platform);
        return;
    };
    let unitPriceDes = lang('按年付费');
    if ((system === 'EN' || system === 'EM') && localeLang === 'cn') {
        unitPriceDes = lang('每年');
    }
    let unitPrice = lang('按席位数付费，已包含10个席位');
    if (system === 'EN' || system === 'EM') {
        unitPrice = lang('每月起') + unitPrice;
    }
    // 商用试用版非cn站隐藏专业版购买入口
    const renderProfessionalBuyButton = () => {
        return (
            <If
                condition={
                    isRegisterUser &&
                    planInfo.plan_id === 3 &&
                    planInfo.teacher_status === VERSION_TYPE.COMMERICAL_TRIAL
                }
            >
                <Button
                    onClick={() => {
                        trackEvent(ACTION_TYPE.CLICK_PROFESSION, DEVICE_FROM_MAP[platform]);
                        onPlan(PLAN_TYPE.PROFESSIONAL, true);
                    }}
                    className="edition-btn"
                    alias="striking"
                >
                    {commonLang('立即购买')}
                </Button>
            </If>
        );

        return;
    };
    return (
        <Fragment>
            <div className="edition-item">
                <div className="edition-item-title">{lang('基本版')}</div>
                <div className="edition-item-des">{lang('适用于个人用户')}</div>
                <div className="edition-item-content edition-item-text">
                    <div className="enterprise-text">{lang('免费')}</div>
                </div>
                <div className="edition-item-footer">
                    <If condition={!isRegisterUser}>
                        <Button onClick={() => onPlan(PLAN_TYPE.BASIC)} className="edition-btn" alias="striking">
                            {commonLang('免费注册')}
                        </Button>
                    </If>
                    <If condition={isRegisterUser && planInfo.plan_id === 1}>
                        <Button onClick={() => onPlan(PLAN_TYPE.BASIC)} className="edition-btn" alias="striking">
                            {commonLang('正在使用')}
                        </Button>
                    </If>
                </div>
            </div>
            <div className="edition-item">
                <div className="edition-item-title">{lang('专业版')}</div>
                <div className="edition-item-des">{lang('适用于个人培训师')}</div>
                <div className="edition-item-content">
                    <div className="edition-unit-price">
                        <If condition={system === 'CN'}>
                            <Fragment>
                                <div className="unit-price-money">￥</div>
                                <div className="unit-price-number">{getPremiumPrice()}</div>
                            </Fragment>
                        </If>
                        <If condition={system === 'EN' || system === 'EM'}>
                            <Fragment>
                                <div className="unit-price-money">$</div>
                                <div className="unit-price-number">{getPremiumPrice()}</div>
                            </Fragment>
                        </If>
                        <If condition={system === 'JP'}>
                            <Fragment>
                                <div className="unit-price-number">{getPremiumPrice()}</div>
                                <div className="unit-price-money">円</div>
                            </Fragment>
                        </If>
                        <If condition={system === 'TW'}>
                            <Fragment>
                                <div className="unit-price-money unit-price-money-tw">NT$</div>
                                <div className="unit-price-number unit-price-number-tw">{getPremiumPrice()}</div>
                            </Fragment>
                        </If>
                        <If condition={system === 'CN' && localeLang === 'cn'}>
                            <div className="unit-price-text">/{lang('年')}</div>
                        </If>
                        <If condition={system === 'CN' && localeLang === 'en'}>
                            <div className="unit-price-text">RMB/Year</div>
                        </If>
                    </div>
                    <div className="edition-unit-des">
                        <If condition={system === 'JP' && localeLang === 'jp'}>
                            <Fragment>
                                （税込）/登録アカウント
                                <br />
                            </Fragment>
                        </If>
                        <If condition={system === 'JP' && localeLang === 'en'}>
                            <Fragment>
                                （tax included）/UMU Pro account
                                <br />
                            </Fragment>
                        </If>
                        {unitPriceDes}
                    </div>
                </div>
                <div className="edition-item-footer">
                    <If condition={!isRegisterUser}>
                        <Button
                            onClick={() => {
                                trackEvent(ACTION_TYPE.CLICK_PROFESSION, DEVICE_FROM_MAP[platform]);
                                onPlan(PLAN_TYPE.PROFESSIONAL, true);
                            }}
                            className="edition-btn"
                            alias="striking"
                        >
                            {commonLang('立即购买')}
                        </Button>
                    </If>
                    <If condition={isRegisterUser && planInfo.plan_id === 1}>
                        <Button
                            onClick={() => {
                                trackEvent(ACTION_TYPE.CLICK_PROFESSION, DEVICE_FROM_MAP[platform]);
                                onPlan(PLAN_TYPE.PROFESSIONAL, true);
                            }}
                            className="edition-btn"
                            alias="striking"
                        >
                            {commonLang('立即购买')}
                        </Button>
                    </If>
                    <If condition={isRegisterUser && planInfo.plan_id === 2}>
                        <Button
                            onClick={() => onPlan(PLAN_TYPE.PROFESSIONAL, true)}
                            className="edition-btn"
                            alias="striking"
                        >
                            {commonLang('正在使用')}
                        </Button>
                    </If>
                    {renderProfessionalBuyButton()}
                </div>
            </div>
            <div className="edition-item">
                <div className="edition-item-title">{lang('团队版')}</div>
                <div className="edition-item-des">{lang('为团队学员购买账户')}</div>
                <div className="edition-item-content">
                    <div className="edition-unit-price">
                        <If condition={system === 'CN'}>
                            <Fragment>
                                <div className="unit-price-money">￥</div>
                                <div className="unit-price-number">{getBusinessPrice()}</div>
                            </Fragment>
                        </If>
                        <If condition={system === 'TW'}>
                            <Fragment>
                                <div className="unit-price-money unit-price-money-tw">NT$</div>
                                <div className="unit-price-number unit-price-number-tw">{getBusinessPrice()}</div>
                            </Fragment>
                        </If>
                        <If condition={system === 'EN' || system === 'EM'}>
                            <Fragment>
                                <div className="unit-price-money">$</div>
                                <div className="unit-price-number">{getBusinessPrice()}</div>
                            </Fragment>
                        </If>
                        <If condition={system === 'JP'}>
                            <Fragment>
                                <div className="unit-price-number">{getBusinessPrice()}</div>
                                <div className="unit-price-money">円</div>
                            </Fragment>
                        </If>
                        <If condition={system === 'CN' && localeLang === 'cn'}>
                            <div className="unit-price-text">/{lang('年')}</div>
                        </If>
                        <If condition={system === 'TW' && localeLang === 'tw'}>
                            <div className="unit-price-text unit-price-text-tw">{lang('每月起')}</div>
                        </If>
                        <If condition={system === 'TW' && localeLang === 'en'}>
                            <div className="unit-price-text unit-price-text-tw">{lang('每月起')}</div>
                        </If>
                    </div>
                    <div className="edition-unit-des">
                        <If condition={system === 'JP' && localeLang === 'jp'}>
                            <Fragment>
                                （税込）/登録アカウント <br /> 毎月
                                <br />
                            </Fragment>
                        </If>
                        <If condition={system === 'JP' && localeLang === 'en'}>
                            <Fragment>
                                （tax included）/account <br /> month
                                <br />
                            </Fragment>
                        </If>
                        {unitPrice}
                    </div>
                </div>
                <div className="edition-item-footer">
                    <If condition={!isRegisterUser}>
                        <Fragment>
                            <Button
                                onClick={() => {
                                    trackEvent(ACTION_TYPE.CLICK_FREE, DEVICE_FROM_MAP[platform]);
                                    redirectToCommericalTrial(platform, isRegisterUser);
                                }}
                                className="edition-btn now-shop-btn"
                            >
                                {commonLang('免费试用')}
                            </Button>
                            <div>
                                <span className="edition-text-btns">{lang('或')}&nbsp;</span>
                                <Button
                                    onClick={() => {
                                        trackEvent(ACTION_TYPE.CLICK_BUSINESS, DEVICE_FROM_MAP[platform]);
                                        onPlan(PLAN_TYPE.COMMERICAL, true);
                                    }}
                                    className="edition-text-btn"
                                    alias="text"
                                >
                                    {commonLang('立即购买')}
                                </Button>
                            </div>
                        </Fragment>
                    </If>
                    <If condition={isRegisterUser && planInfo.plan_id < 3}>
                        <Fragment>
                            <Button
                                onClick={() => {
                                    trackEvent(ACTION_TYPE.CLICK_BUSINESS, DEVICE_FROM_MAP[platform]);
                                    onPlan(PLAN_TYPE.COMMERICAL, true);
                                }}
                                className="edition-btn now-shop-btn"
                            >
                                {commonLang('立即购买')}
                            </Button>
                            <If condition={planInfo.business_free_trial === 1}>
                                <Fragment>
                                    <span className="edition-text-btns">{lang('或')}&nbsp;</span>
                                    <Button
                                        onClick={() => {
                                            trackEvent(ACTION_TYPE.CLICK_FREE, DEVICE_FROM_MAP[platform]);
                                            redirectToCommericalTrial(platform, isRegisterUser);
                                        }}
                                        className="edition-text-btn"
                                        alias="text"
                                    >
                                        {commonLang('免费试用')}
                                    </Button>
                                </Fragment>
                            </If>
                        </Fragment>
                    </If>
                    <If
                        condition={
                            isRegisterUser &&
                            planInfo.plan_id === 3 &&
                            planInfo.teacher_status === VERSION_TYPE.COMMERICAL
                        }
                    >
                        <Button className="edition-btn" alias="striking" onClick={redirectInCommerical}>
                            {commonLang('正在使用')}
                        </Button>
                    </If>
                    <If
                        condition={
                            isRegisterUser &&
                            planInfo.plan_id === 3 &&
                            planInfo.teacher_status === VERSION_TYPE.COMMERICAL_TRIAL
                        }
                    >
                        <Button
                            onClick={() => {
                                trackEvent(ACTION_TYPE.CLICK_BUSINESS, DEVICE_FROM_MAP[platform]);
                                onPlan(PLAN_TYPE.COMMERICAL, true);
                            }}
                            className="edition-btn now-shop-btn"
                        >
                            {commonLang('立即购买')}
                        </Button>
                    </If>
                </div>
            </div>
            <div className="edition-item">
                <div className="edition-item-title">{lang('企业版')}</div>
                <div className="edition-item-des">{lang('适用于大型企业或集团')}</div>
                <div className="edition-item-content edition-item-text">
                    <div className="enterprise-text"></div>
                </div>
                <div className="edition-item-footer">
                    <If condition={(isRegisterUser && planInfo.plan_id < 4) || !isRegisterUser}>
                        <Button
                            onClick={() => {
                                setStatLogDialogs({ page_type: 16, action_type: '3' });
                                trackEvent(ACTION_TYPE.CLICK_CONTRACT, DEVICE_FROM_MAP[platform]);
                                redirectToContractSales(platform);
                            }}
                            className="edition-btn"
                            alias="striking"
                        >
                            {commonLang('联系销售')}
                        </Button>
                    </If>
                    <If condition={isRegisterUser && (planInfo.plan_id === 4 || planInfo.plan_id === 5)}>
                        <Button className="edition-btn" alias="striking">
                            {commonLang('正在使用')}
                        </Button>
                    </If>
                </div>
            </div>
        </Fragment>
    );
};
